import { useState } from "react";
import { addDays, format, startOfWeek, isSameDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { useUpdateItem } from "@/hooks/useItems";
import { useCreateCalendarEvent } from "@/hooks/useCalendar";
import { toast } from "sonner";
import type { Item } from "@/lib/types";

type Props = {
  item: Item;
  onDone: () => void;
};

export function ScheduleView({ item, onDone }: Props) {
  const updateItem = useUpdateItem();
  const createCalEvent = useCreateCalendarEvent();
  const [showCalendar, setShowCalendar] = useState(false);
  const [addToGcal, setAddToGcal] = useState(false);
  const [selectedTime, setSelectedTime] = useState("09:00");

  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  // Only show today + future days this week
  const futureDays = weekDays.filter((d) => d >= today || isSameDay(d, today));

  const schedule = async (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");

    // Update the item's date_target
    updateItem.mutate(
      { id: item.id, date_target: dateStr },
      {
        onSuccess: async () => {
          toast(`Scheduled for ${format(date, "EEE, MMM d")}`);

          // Also create Google Calendar event if toggled
          if (addToGcal) {
            try {
              const startTime = new Date(`${dateStr}T${selectedTime}:00`).toISOString();
              const endTime = new Date(
                new Date(`${dateStr}T${selectedTime}:00`).getTime() + 60 * 60 * 1000
              ).toISOString();
              await createCalEvent.mutateAsync({
                title: item.title,
                start_time: startTime,
                end_time: endTime,
              });
              toast("Added to Google Calendar");
            } catch {
              toast.error("Failed to add to calendar — check Google connection in Settings");
            }
          }

          onDone();
        },
      }
    );
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">Schedule for...</h4>
      <div className="flex gap-1.5 flex-wrap">
        {futureDays.map((day) => {
          const isToday = isSameDay(day, today);
          return (
            <button
              key={day.toISOString()}
              onClick={() => schedule(day)}
              disabled={updateItem.isPending}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${
                isToday
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted border-border hover:border-primary/50"
              }`}
            >
              <div>{format(day, "EEE")}</div>
              <div className="text-[10px] opacity-70">{format(day, "d")}</div>
            </button>
          );
        })}
      </div>

      {/* Google Calendar toggle */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => setAddToGcal(!addToGcal)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${
            addToGcal
              ? "bg-blue-500/10 border-blue-500/30 text-blue-500"
              : "bg-muted border-border text-muted-foreground hover:border-primary/30"
          }`}
        >
          <span>{addToGcal ? "✓" : "+"}</span>
          Add to Google Calendar
        </button>
        {addToGcal && (
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="text-xs bg-muted border border-border rounded px-2 py-1"
          />
        )}
      </div>

      {!showCalendar ? (
        <button
          onClick={() => setShowCalendar(true)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Pick another date...
        </button>
      ) : (
        <Calendar
          mode="single"
          selected={item.date_target ? new Date(item.date_target + "T00:00:00") : undefined}
          onSelect={(date) => date && schedule(date)}
          disabled={(date) => date < today}
        />
      )}
    </div>
  );
}
